import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Crown, Star, UserPlus } from 'lucide-react';
import { FollowButton } from '@/components/profile/FollowButton';

interface TopSupporter {
  id: string;
  from_user_id: string;
  display_name: string;
  avatar_url?: string;
  totalTips: number;
  tipCount: number;
}

export const FanInsights = () => {
  const { user } = useAuth();
  const [topSupporters, setTopSupporters] = useState<TopSupporter[]>([]);
  const [avgTipSize, setAvgTipSize] = useState(0);
  const [repeatSupporters, setRepeatSupporters] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      loadFanInsights();
    }
  }, [user]);

  const loadFanInsights = async () => {
    if (!user) return;

    try {
      // Get tips data grouped by supporter
      const { data: tips, error } = await supabase
        .from('tips')
        .select(`
          from_user_id,
          amount,
          profiles!tips_from_user_id_fkey(
            id,
            display_name,
            avatar_url
          )
        `)
        .eq('to_user_id', user.id);

      if (error) throw error;

      if (tips && tips.length > 0) {
        // Group by supporter and calculate totals
        const supporterMap = new Map<string, TopSupporter>();
        let totalAmount = 0;

        tips.forEach((tip: any) => {
          const userId = tip.from_user_id;
          totalAmount += tip.amount;

          if (!supporterMap.has(userId)) {
            supporterMap.set(userId, {
              id: userId,
              from_user_id: userId,
              display_name: tip.profiles?.display_name || 'Unknown',
              avatar_url: tip.profiles?.avatar_url,
              totalTips: 0,
              tipCount: 0
            });
          }

          const supporter = supporterMap.get(userId)!;
          supporter.totalTips += tip.amount;
          supporter.tipCount += 1;
        });

        // Convert to array and sort by total tips
        const supportersArray = Array.from(supporterMap.values())
          .sort((a, b) => b.totalTips - a.totalTips)
          .slice(0, 5);

        setTopSupporters(supportersArray);
        setAvgTipSize(Math.round(totalAmount / tips.length));
        setRepeatSupporters(Array.from(supporterMap.values()).filter(s => s.tipCount > 1).length);
      }
    } catch (error) {
      console.error('Error loading fan insights:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-center text-muted-foreground">Loading insights...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Star className="h-5 w-5" />
          Fan Insights
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Quick Stats */}
        <div className="grid grid-cols-2 gap-4">
          <div className="p-4 bg-muted rounded-lg">
            <p className="text-sm text-muted-foreground">Avg. Tip Size</p>
            <p className="text-2xl font-bold">{avgTipSize} coins</p>
          </div>
          <div className="p-4 bg-muted rounded-lg">
            <p className="text-sm text-muted-foreground">Repeat Supporters</p>
            <p className="text-2xl font-bold">{repeatSupporters}</p>
          </div>
        </div>

        {/* Top Supporters */}
        <div className="space-y-3">
          <h4 className="text-sm font-medium flex items-center gap-2">
            <Crown className="h-4 w-4 text-accent" />
            Top Supporters
          </h4>
          {topSupporters.length === 0 ? (
            <div className="text-center py-8">
              <UserPlus className="h-12 w-12 mx-auto text-muted-foreground/50 mb-2" />
              <p className="text-sm text-muted-foreground">No supporters yet</p>
            </div>
          ) : (
            topSupporters.map((supporter, index) => (
              <div key={supporter.id} className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                <div className="flex items-center gap-3 flex-1">
                  <Badge variant={index === 0 ? 'default' : 'secondary'} className="w-6 h-6 flex items-center justify-center p-0">
                    {index + 1}
                  </Badge>
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={supporter.avatar_url} />
                    <AvatarFallback>{supporter.display_name[0]}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{supporter.display_name}</p>
                    <p className="text-xs text-muted-foreground">{supporter.tipCount} tips sent</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold">{supporter.totalTips}</p>
                  <p className="text-xs text-muted-foreground">coins</p>
                </div>
                {user && <FollowButton userId={supporter.from_user_id} size="sm" variant="outline" />}
              </div>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
};